import {
  Calendar,
  ShoppingCart,
  UserPlus,
  Receipt,
  CalendarDays,
  Users,
  Briefcase,
  Package,
  DollarSign,
  Search,
  FileText,
  Sun,
  Moon,
  Download,
  type LucideIcon,
} from 'lucide-react';
import { type UserRole } from '@/lib/integrations/supabase/types';

export type ActionCategory = 'create' | 'navigate' | 'search' | 'toggle' | 'export';
export type ActionSeverity = 'blocking' | 'important' | 'suggestion' | 'info';

export interface AppAction {
  id: string;
  label: string;
  translationKey?: string; // i18n key for translation
  shortLabel?: string; // For compact display
  icon: LucideIcon;
  category: ActionCategory;
  route?: string;
  handler?: () => void | Promise<void>;
  permission?: UserRole[]; // Who can see/execute
  keywords?: string[]; // For fuzzy search
  priority?: number; // For sorting (higher = more important)
}

export interface ActionInsight {
  id: string;
  message: string;
  severity: ActionSeverity;
  action?: AppAction; // Related action
  route?: string; // Where to go to fix
  metadata?: Record<string, any>;
}

// Severity priority mapping (lower number = higher priority)
export const SEVERITY_PRIORITY: Record<ActionSeverity, number> = {
  blocking: 1,
  important: 2,
  suggestion: 3,
  info: 4,
};

// Create actions
export const CREATE_ACTIONS: AppAction[] = [
  {
    id: 'create-booking',
    label: 'Create Booking',
    translationKey: 'commandPalette.actions.createBooking',
    shortLabel: 'Booking',
    icon: Calendar,
    category: 'create',
    keywords: ['book', 'appointment', 'schedule', 'new booking'],
    permission: ['owner', 'manager', 'front_desk'],
    priority: 10,
  },
  {
    id: 'create-sale',
    label: 'Create Sale / Offer',
    translationKey: 'commandPalette.actions.createSale',
    shortLabel: 'Sale',
    icon: ShoppingCart,
    category: 'create',
    keywords: ['sale', 'offer', 'payment', 'checkout', 'purchase'],
    permission: ['owner', 'manager', 'front_desk'],
    priority: 9,
  },
  {
    id: 'create-customer',
    label: 'Add Customer',
    translationKey: 'commandPalette.actions.addCustomer',
    shortLabel: 'Customer',
    icon: UserPlus,
    category: 'create',
    route: '/customers',
    keywords: ['customer', 'client', 'add', 'new customer'],
    permission: ['owner', 'manager', 'front_desk'],
    priority: 8,
  },
  {
    id: 'create-expense',
    label: 'Add Expense',
    translationKey: 'commandPalette.actions.addExpense',
    shortLabel: 'Expense',
    icon: Receipt,
    category: 'create',
    route: '/expenses/create',
    keywords: ['expense', 'cost', 'spending', 'payment', 'bill'],
    permission: ['owner', 'manager'],
    priority: 7,
  },
];

// Navigate actions
export const NAVIGATE_ACTIONS: AppAction[] = [
  {
    id: 'nav-schedule',
    label: 'Go to Schedule',
    translationKey: 'commandPalette.actions.goToSchedule',
    shortLabel: 'Schedule',
    icon: CalendarDays,
    category: 'navigate',
    route: '/schedule',
    keywords: ['schedule', 'calendar', 'appointments', 'bookings', 'today'],
    priority: 10,
  },
  {
    id: 'nav-customers',
    label: 'Go to Customers',
    translationKey: 'commandPalette.actions.goToCustomers',
    shortLabel: 'Customers',
    icon: Users,
    category: 'navigate',
    route: '/customers',
    keywords: ['customers', 'clients', 'people', 'contacts'],
    priority: 9,
  },
  {
    id: 'nav-staff',
    label: 'Go to Staff',
    translationKey: 'commandPalette.actions.goToStaff',
    shortLabel: 'Staff',
    icon: Briefcase,
    category: 'navigate',
    route: '/staff',
    keywords: ['staff', 'employees', 'barbers', 'team'],
    priority: 8,
  },
  {
    id: 'nav-inventory',
    label: 'Go to Inventory',
    translationKey: 'commandPalette.actions.goToInventory',
    shortLabel: 'Inventory',
    icon: Package,
    category: 'navigate',
    route: '/inventory',
    keywords: ['inventory', 'stock', 'products', 'items', 'supplies'],
    priority: 7,
  },
  {
    id: 'nav-sales',
    label: 'Go to Sales',
    translationKey: 'commandPalette.actions.goToSales',
    shortLabel: 'Sales',
    icon: DollarSign,
    category: 'navigate',
    route: '/sales',
    keywords: ['sales', 'revenue', 'earnings', 'transactions', 'payments'],
    priority: 6,
  },
];

// Search actions
export const SEARCH_ACTIONS: AppAction[] = [
  {
    id: 'search-customer',
    label: 'Search Customer',
    translationKey: 'commandPalette.actions.searchCustomer',
    icon: Search,
    category: 'search',
    keywords: ['search', 'find', 'customer', 'client', 'phone', 'name'],
    priority: 10,
  },
  {
    id: 'search-booking',
    label: 'Search Booking',
    translationKey: 'commandPalette.actions.searchBooking',
    icon: Search,
    category: 'search',
    keywords: ['search', 'find', 'booking', 'appointment'],
    priority: 9,
  },
];

// Export actions
export const EXPORT_ACTIONS: AppAction[] = [
  {
    id: 'export-bookings',
    label: 'Export Bookings',
    translationKey: 'commandPalette.actions.exportBookings',
    icon: Download,
    category: 'export',
    keywords: ['export', 'download', 'bookings', 'excel', 'csv'],
    permission: ['owner', 'manager'],
    priority: 5,
  },
  {
    id: 'export-sales',
    label: 'Export Sales',
    translationKey: 'commandPalette.actions.exportSales',
    icon: Download,
    category: 'export',
    keywords: ['export', 'download', 'sales', 'revenue', 'excel', 'csv'],
    permission: ['owner', 'manager'],
    priority: 4,
  },
];

// All actions combined
export const ALL_ACTIONS: AppAction[] = [
  ...CREATE_ACTIONS,
  ...NAVIGATE_ACTIONS,
  ...SEARCH_ACTIONS,
  ...EXPORT_ACTIONS,
];

// Helper to get actions by category
export function getActionsByCategory(category: ActionCategory): AppAction[] {
  return ALL_ACTIONS.filter((action) => action.category === category);
}

// Helper to get actions filtered by user role
export function getActionsForRole(role: UserRole): AppAction[] {
  return ALL_ACTIONS.filter((action) => {
    // If no permission specified, it's available to all
    if (!action.permission || action.permission.length === 0) {
      return true;
    }
    return action.permission.includes(role);
  });
}

// Helper to search actions by query (fuzzy match)
export function searchActions(query: string, role?: UserRole): AppAction[] {
  const lowerQuery = query.toLowerCase().trim();

  if (!lowerQuery) return [];

  const actions = role ? getActionsForRole(role) : ALL_ACTIONS;

  return actions
    .filter((action) => {
      // Search in label
      if (action.label.toLowerCase().includes(lowerQuery)) return true;
      if (action.shortLabel?.toLowerCase().includes(lowerQuery)) return true;

      // Search in keywords
      if (action.keywords?.some((kw) => kw.toLowerCase().includes(lowerQuery))) {
        return true;
      }

      return false;
    })
    .sort((a, b) => (b.priority || 0) - (a.priority || 0)); // Sort by priority
}

// Helper to get action by ID
export function getActionById(id: string): AppAction | undefined {
  return ALL_ACTIONS.find((action) => action.id === id);
}
